import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import memoize from "lodash.memoize";

const slice = createSlice({
  name: "buzzBucks",
  initialState: { 
    currentBalances: [],
    mainPool: null,
    distributorPool: null,
    userHistory: []
  },
  reducers: {
    
    // we do this all in one reducer to avoid state updates
    buzzBucksBalancesInitialised: (state, action) => {
      state.currentBalances = action.payload.data.map(userData => ({
        id: userData.Id, 
        userId: userData.AppUserId,
        balance: userData.CurrentBalance,
        lifetimeReceived: userData.LifetimeReceived,
        lifetimeTradedIn: userData.LifetimeTradedIn
      }));
    },

    buzzBucksUserBalanceUpdated: (state, action) => {
      const balanceData = action.payload.data;
      const index = state.currentBalances.findIndex(
        (balance) => balance.userId === balanceData.AppUserId
      );
      if (index >= 0) {
        state.currentBalances[index] = {
          ...state.currentBalances[index],
          id: balanceData.Id,
          balance: balanceData.CurrentBalance,
          lifetimeReceived: balanceData.LifetimeReceived,
          lifetimeTradedIn: balanceData.LifetimeTradedIn
        };
      } else {
        // If balance doesn't exist, add it
        state.currentBalances.push({
          id: balanceData.Id,
          userId: balanceData.AppUserId,
          balance: balanceData.CurrentBalance,
          lifetimeReceived: balanceData.LifetimeReceived,
          lifetimeTradedIn: balanceData.LifetimeTradedIn
        });
      }
    },

    buzzBucksCurrentMainPoolInitialised: (state, action) => {
      const poolData = action.payload.data;
      state.mainPool = {
        id: poolData.Id,
        month: poolData.Month,
        year: poolData.Year,
        totalAmount: poolData.TotalAmount,
        withDistributors: poolData.WithDistributors,
        withUsers: poolData.WithUsers,
        remainingAmount: poolData.RemainingAmount
      };
    },

    buzzBucksDistributorPoolInitialised: (state, action) => {
      const poolData = action.payload.data;
      state.distributorPool = {
        id: poolData.Id,
        availableBalance: poolData.Available,
        totalReceived: poolData.Received,
        totalDistributed: poolData.Distributed
      };
    },

    buzzBucksUserHistoryInitialised: (state, action) => {
      state.userHistory = action.payload.data.map(historyData => ({
        id: historyData.Id,
        userAvatarId: historyData.UserAvatarId,
        amount: historyData.Amount,
        utcCreatedDateTime: historyData.UtcCreatedDateTime,
        details: historyData.Details,
        message: historyData.Message
      }));
    },

    buzzBucksUserHistoryTransactionAdded: (state, action) => {
      const transaction = action.payload.data;
      state.userHistory.push({
        id: transaction.Id,
        userAvatarId: transaction.UserAvatarId,
        amount: transaction.Amount,
        utcCreatedDateTime: transaction.UtcCreatedDateTime,
        details: transaction.Details,
        message: transaction.Message
      });
    },

  },
});

export const { buzzBucksBalancesInitialised, buzzBucksCurrentMainPoolInitialised, buzzBucksDistributorPoolInitialised, buzzBucksUserBalanceUpdated, buzzBucksUserHistoryInitialised, buzzBucksUserHistoryTransactionAdded } = slice.actions;
export default slice.reducer;

// Selector to get BuzzBucks balance for a specific user
export const getBuzzBucksBalanceByUserId = createSelector(
  (state) => state.entities.buzzBucks,
  (buzzBucks) =>
    memoize((userId) => {
      return buzzBucks.currentBalances.find((balance) => balance.userId === userId) || null;
    })
);

// Selector to get the main pool
export const getBuzzBucksMainPool = createSelector(
  (state) => state.entities.buzzBucks,
  (buzzBucks) => buzzBucks.mainPool
);

// Selector to get the distributor pool
export const getBuzzBucksDistributorPool = createSelector(
  (state) => state.entities.buzzBucks,
  (buzzBucks) => buzzBucks.distributorPool
);

// Selector to get the user history
export const getBuzzBucksUserHistory = createSelector(
  (state) => state.entities.buzzBucks,
  (buzzBucks) => buzzBucks.userHistory
);
